/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import React, {
  useContext, useState, useEffect, useRef, forwardRef,
} from 'react';
import PropTypes from 'prop-types';
import { LocaleContext } from '@/contexts/locale-context';
import { TelemetryContext } from '@/contexts/telemetry-context';
import ErrorReportingHOC from '@/components/error-reporting-hoc';
import Tooltip from '@/components/tooltip';
import TextDiv from '@/components/text-div';
import TooltipIcon from '@/public/icons/tooltip.svg';
import SimpleButton from '@/components/button-simple/button-simple';
import { escapeCustomText, unEscapeCustomText } from '@/modules/page-customisation/utilities';

/**
 * TextArea contains an input field, header, error message, and tooltip.
 * Is wrapped in the ErrorReporting HOC.
 * @param {string} inputSelectorId - DOM id, used to identify this element
 * @param {string} title - Displayed above the text field
 * @param {string} placeholder - Placeholder text
 * @param {JSX.Element} tooltipContent - If present, show a tooltip button that uses this as content
 * @param {number} inputWidth - CSS Percentage width. Defaults to 100
 * @param {number} inputHeight - CSS height. Defaults to 96px
 * @param {number} inputMaxHeight - CSS Max height. Defaults to null
 * @param {boolean} required - Whether or not this is a required field
 * @param {function} onChange - Optional function called when the input value changes
 * @param {Object} validationRules - Full description in error-reporting.jsx
 * @param {string} error - Passed in automatically, no need to set
 * @param {string} defaultValue - Optional. Input field value on component load
 * @param {function} onBlur - Called when the textarea is moved away from
 * @param {boolean} hasCopyButton - Whether or not to show a copy button
 * @param {boolean} showCount - Whether or not to show a character count
 * @param {string} selected - The currently selected input
 * @param {function} onFocus - Called when the textarea is focused
 * @param {boolean} disabled - Whether or not the input is disabled
 * @param {boolean} horizontalLayout - Whether or not to display the title and input horizontally
 * @param {string} textAreaMinWidth - Minimum width of the textarea, optional
 * @param {number} rows - Number of rows to show in the textarea. Defaults to 5
 * @returns {JSX.Element}
 */
const TextArea = forwardRef(({
  inputSelectorId,
  title,
  placeholder,
  tooltipContent,
  inputWidth,
  inputHeight,
  inputMaxHeight,
  maxLength,
  required,
  onChange,
  onBlur,
  disabled,
  horizontalLayout,
  content, // Passed in automatically, no need to set
  setContent, // Passed in automatically, no need to set
  error, // Passed in automatically, no need to set
  showCount,
  selected,
  onFocus,
  hasCopyButton,
  textAreaMinWidth,
  telemetryEvent,
  telemetryValue,
  telemetryCustomProperty,
  ariaLabel,
  rows,
}, ref) => {
  const { textDirection, translate } = useContext(LocaleContext);
  const telemetryContext = useContext(TelemetryContext);
  const [chars, setChars] = useState(0);
  const inputRef = useRef(null);

  const inputChanged = (value) => {
    setContent(value);
    onChange(escapeCustomText(value));
  };

  const sendTelemtryEvent = (value) => {
    if (telemetryEvent) {
      telemetryContext?.sendEvent(telemetryEvent, {
        ...telemetryValue && ({ value_changed: telemetryValue }),
        ...telemetryCustomProperty && ({ [telemetryCustomProperty]: value }),
      });
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
  };

  useEffect(() => {
    if (selected === inputSelectorId) {
      inputRef?.current?.focus();
    }
  }, [selected]);

  useEffect(() => {
    setChars(content.length);
  }, [content]);

  return (
    <div className={`text-area ${horizontalLayout ? 'horizontal' : ''}`}>
      {title || tooltipContent ? (
        <div className="title-wrapper" dir={textDirection}>
          {title && (
            <div className="title">
              <TextDiv>
                {`${title}${required === true ? '*' : ''}`}
              </TextDiv>
            </div>
          )}
          {tooltipContent && (
            <Tooltip
              content={tooltipContent}
              triggerOnClick
            >
              <div
                className="tooltip-button-wrapper"
                aria-label={translate('constants.tooltipLabels.questionMark')}
                role="button"
                tabIndex={0}
              >
                <TooltipIcon />
              </div>
            </Tooltip>
          )}
        </div>
      ) : null}
      <div className="input">
        <div className="text-area-wrapper">
          <textarea
            aria-label={ariaLabel}
            id={inputSelectorId}
            type="text"
            dir={textDirection}
            placeholder={placeholder}
            onChange={(event) => inputChanged(event.target.value)}
            onBlur={(event) => {
              onBlur(event.target.value);
              sendTelemtryEvent(event.target.value);
            }}
            required={required}
            value={unEscapeCustomText(content) || ''}
            className={error ? 'text-area-error' : ''}
            rows={rows || 5}
            disabled={disabled}
            ref={inputRef}
            onFocus={() => onFocus(inputSelectorId)}
          />
        </div>
      </div>
      {hasCopyButton && (
        <div className="copy-button-wrapper">
          <SimpleButton
            hasMargin={false}
            isLozenge
            largeButton
            fontSize="--font-size-xs"
            borderColor="--color-medium-grey"
            onClick={handleCopy}
          >
            {translate('details.copyButton')}
          </SimpleButton>
        </div>
      )}
      <div className="input-messages" ref={ref}>
        <div className={`error ${error ? ' visible' : ''} ${textDirection === 'rtl' ? ' rtl' : ''}`}>
          {error && <TextDiv>{error}</TextDiv>}
        </div>
        {chars > 0 && showCount && (
          <div className="character-count">
            {`${chars}/${maxLength}`}
          </div>
        )}
      </div>

      <style jsx>
        {`
          .copy-button-wrapper {
            display: flex;
            justify-content: flex-end;
            flex: 1;
            margin-top: 10px;
          }
          .error {
            min-height: 20px;
            margin-top: 3px;
            visibility: hidden;
            white-space: break-spaces;
          }

          .error.visible {
            color: var(--color-error-red);
            font-size: var(--font-size-error);
            visibility: visible;
          }

          .error.rtl {
            text-align: left;
          }

          .text-area {
            display: flex;
            flex-wrap: wrap;
            position: relative;
            width: ${inputWidth}%;
          }

          .input {
            flex: ${horizontalLayout ? 1 : '0 1 100%'};
          }

          .tooltip-button-wrapper {
            align-items: center;
            display: flex;
            justify-content: center;
            margin-bottom: 3px;
            width: 35px;
          }

          .character-count {
            text-align: right;
            font-size: var(--font-size-error);
          }


          .input-messages {
            display: flex;
            justify-content: space-between;
            flex: 0 1 100%;
            white-space: break-spaces;
          }

          .input-messages .error {
            flex-basis: 100%;
            word-break: break-word;
          }

          @media only screen and (max-width: 768px) {
            .text-area {
              width: 100%;
            }
          }

          .text-area-wrapper {
            position: relative;
          }

          textarea {
            border-radius: var(--border-radius);
            border: 1px solid var(--color-mid-grey);
            color: ${disabled ? 'var(--color-dark-grey)' : 'var(--color-primary-dark)'};
            font-size: var(--font-size-body);
            font-weight: 400;
            line-height: 24px;
            min-height: ${inputHeight}px;
            max-height: ${inputMaxHeight}px;
            padding: 10px;
            resize: vertical;
            scroll-behavior: smooth;
            scrollbar-color: var(--color-mid-grey) var(--color-white);
            scrollbar-width: thin;
            width: 100%;
          }

          textarea::placeholder {
            color: var(--color-mid-grey);
            line-height: 24px;
          }

          textarea:disabled { 
            color: var(--color-mid-grey);
            background-color: var(--color-white);
            border-color: var(--color-light-deeper-grey);
          }

          .text-area-error {
            border: 1px solid var(--color-error-red);
          }

          .title {
            color: var(--color-primary-dark);
            display: block;
            font-size: var(--font-size-H2);
            font-weight: var(--font-weight-H2);
            line-height: 2;
          }

          .title-wrapper {
            align-items: center;
            display: flex;
          }

          .horizontal {
            gap: 16px;
          }

          .horizontal .title {
            font-weight: normal;
            font-size: var(--font-size-tooltip);
          }

          .horizontal textarea {
            font-size: var(--font-size-tooltip);
            min-width: ${textAreaMinWidth || 'unset'};
          }

          @media only screen and (min-width: 768px) {
            .horizontal .title {
              margin-bottom: 36px;
            }
          }
        `}
      </style>
    </div>
  );
});

TextArea.defaultProps = {
  inputSelectorId: null,
  title: null,
  placeholder: '',
  tooltipContent: null,
  inputWidth: 100,
  inputHeight: 96,
  inputMaxHeight: null,
  maxLength: null,
  required: false,
  disabled: false,
  horizontalLayout: false,
  validationRules: {
    onSubmit: null,
    required: '',
    submitErrors: null,
    tooLongError: '',
  },
  onChange: () => { },
  onBlur: () => { },
  error: null,
  content: '',
  showCount: false,
  selected: null,
  onFocus: () => { },
  hasCopyButton: false,
  textAreaMinWidth: null,
  telemetryEvent: null,
  telemetryValue: null,
  telemetryCustomProperty: null,
  ariaLabel: null,
  rows: 5,
};

TextArea.propTypes = {
  inputSelectorId: PropTypes.string,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  tooltipContent: PropTypes.node,
  inputWidth: PropTypes.number,
  inputHeight: PropTypes.number,
  inputMaxHeight: PropTypes.number,
  maxLength: PropTypes.number,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  horizontalLayout: PropTypes.bool,
  validationRules: PropTypes.shape({
    onSubmit: PropTypes.arrayOf(PropTypes.string),
    requiredError: PropTypes.string,
    submitErrors: PropTypes.arrayOf(PropTypes.string),
    tooLongError: PropTypes.string,
  }),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  content: PropTypes.string,
  setContent: PropTypes.func.isRequired,
  error: PropTypes.string,
  showCount: PropTypes.bool,
  selected: PropTypes.string,
  onFocus: PropTypes.func,
  hasCopyButton: PropTypes.bool,
  textAreaMinWidth: PropTypes.string,
  telemetryEvent: PropTypes.string,
  telemetryValue: PropTypes.string,
  telemetryCustomProperty: PropTypes.string,
  ariaLabel: PropTypes.string,
  rows: PropTypes.number,
};

export default ErrorReportingHOC(TextArea);
